import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";

const ROLE_LABEL = {
  super_admin: "Super Admin", operations: "Operations", finance: "Finance",
  support: "Support", marketing: "Marketing", content: "Content", analytics: "Analytics",
};

export default function AdminUsers() {
  const [data, setData] = useState({ admins: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", admin_role: "operations" });
  const [creating, setCreating] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/admins").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Enter name, email and a 6+ char password");
    setCreating(true);
    try { await api.post("/admin/admins", form); toast.success("Admin created"); setForm({ name: "", email: "", password: "", admin_role: "operations" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };
  const changeRole = async (id, admin_role) => { try { await api.put(`/admin/admins/${id}`, { admin_role }); toast.success("Role updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/admins/${id}`); toast.success("Admin removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-coral" /></div>;

  return (
    <div data-testid="admin-users">
      <div className="bg-white rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="text-coral" size={20} /><h2 className="font-display font-bold text-lg">Admin Users &amp; Roles</h2></div>
        <div className="grid sm:grid-cols-5 gap-2 mb-4">
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className="bg-surface rounded-lg px-3 py-2 text-sm" data-testid="new-admin-name" />
          <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="bg-surface rounded-lg px-3 py-2 text-sm" data-testid="new-admin-email" />
          <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" className="bg-surface rounded-lg px-3 py-2 text-sm" data-testid="new-admin-password" />
          <select value={form.admin_role} onChange={(e) => setForm((f) => ({ ...f, admin_role: e.target.value }))} className="bg-surface rounded-lg px-3 py-2 text-sm" data-testid="new-admin-role">{data.roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}</select>
          <button onClick={create} disabled={creating} className="bg-coral text-white font-bold rounded-lg text-sm flex items-center justify-center gap-1" data-testid="create-admin-btn">{creating ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Create</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-600 border-b border-border">{["Name", "Email", "Role", ""].map((c) => <th key={c} className="py-2 font-semibold">{c}</th>)}</tr></thead>
            <tbody>
              {data.admins.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0" data-testid={`admin-row-${a.id}`}>
                  <td className="py-2 font-semibold">{a.name}</td>
                  <td className="py-2">{a.email}</td>
                  <td className="py-2">
                    <select value={a.admin_role || "super_admin"} onChange={(e) => changeRole(a.id, e.target.value)} className="bg-surface rounded-lg px-2 py-1 text-xs" data-testid={`admin-role-${a.id}`}>
                      {data.roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                    </select>
                  </td>
                  <td className="py-2"><button onClick={() => del(a.id)} className="text-red-500" data-testid={`admin-del-${a.id}`}><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
